<template>
  <div class="tiers">
    <div class="label f8">{{ $t("Select Length") }}</div>
    <div class="pills">
      <div class="tiergrid">
        <div
          class="tier"
          v-for="tier in tiers"
          :key="tier.id"
          :class="{ '-conflict': conflictTierIds.includes(tier.id) }"
        >
          <UIButton
            class="tierbutton"
            ghost
            @click="tierAction(tier)"
            :class="{ '-active': isTierSelected(tier.id) }"
          >{{ tier.length }} {{ $t('Min') }}<span v-if="!isOrganizationEducation"> - {{ toLocationLocale(tier.price) }}</span></UIButton>
          <span class="conflictmessage f7">{{ $t('Conflict') }}</span>
        </div>
        <UIButton
          ghost
          class="customtier"
          :class="{ '-active': isTierSelected('custom') }"
          @click="customButtonAction"
        >{{ $t('Custom') }}</UIButton>
      </div>
    </div>
    <div class="customtierinputs" v-if="isTierSelected('custom')">
      <UIInput
        class="customlength"
        type="number"
        :title="$t('Custom Length in Minutes')"
        min="0"
        max="99"
        @input="setCustomLength"
        :value="bookingStationTime.tier.length"
      />
      <UIInput
        v-if="!isOrganizationEducation"
        class="customprice"
        type="number"
        :title="`${$t('Custom Price in')} ${location.currency}`"
        min="0"
        max="99"
        v-model="customPrice"
      />
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import Component from 'vue-class-component';
import ContentBlock from '@/core/shared/components/ContentBlock';
import conflictDetectorWorker from '@/location/conflict-detector.worker';
import UIButton from '@/core/shared/components/ui/UIButton';
import UIInput from '@/core/shared/components/ui/UIInput';
import { isEqual } from 'lodash-es';

const conflictDetector = conflictDetectorWorker();

@Component({
  components: {
    ContentBlock,
    UIButton,
    UIInput,
  },
  props: {
    startTime: {
      type: String,
      required: true,
    },
    location: {
      type: Object,
      required: true,
    },
    booking: {
      type: Object,
      required: true,
    },
    bookingStationTime: {
      type: Object,
      required: true,
    },
    tiers: {
      type: Array,
      required: true,
    },
    bookings: {
      type: Array,
      required: true,
    },
    isOrganizationEducation: {
      type: Boolean,
      default: false,
    },
  },
  watch: {
    startTime: 'generateConflictsTiers',
    'bookingStationTime.station': 'generateConflictsTiers',
  },
})
export default class Tier extends Vue {
  conflictTierIds = [];

  get customPrice () {
    return this.bookingStationTime.tier.price / 100;
  }

  set customPrice (value) {
    this.$emit('tierAction', { ...this.bookingStationTime.tier, price: Math.round(value * 100) });
  }

  setCustomLength (e) {
    this.$emit('tierAction', { ...this.bookingStationTime.tier, length: parseInt(e.target.value) });
  }

  isTierSelected (tierId) {
    return this.bookingStationTime.tier.id === tierId;
  }

  tierAction (tier) {
    this.$emit('tierAction', tier);
  }

  customButtonAction () {
    const tier = { ...this.tiers[0] };
    tier.id = 'custom';
    this.$emit('tierAction', tier);
  }

  async generateConflictsTiers () {
    const newConflictTierIds = [];
    for (let index = 0; index < this.tiers.length; index++) {
      const tier = this.tiers[index];
      const wouldCauseConflict = await conflictDetector.willStartTimeStationAndTierCauseConflict(this.startTime, { ...this.bookingStationTime.station }, tier, this.booking._id, this.bookings);
      if (wouldCauseConflict) newConflictTierIds.push(tier.id);
    }
    if (!isEqual(this.conflictTierIds, newConflictTierIds)) this.conflictTierIds = newConflictTierIds;
  }

  toLocationLocale (number) {
    return this.$store.getters.formatAsLocationCurrency({ locationId: this.location.id, number });
  }
}
</script>

<style scoped lang="postcss">
@import "core/shared/styles";

.tiers {
  & .label {
    margin-bottom: var(--spacingXS);
  }

  & .tiergrid {
    flex: 1 1 auto;
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: 2rem;
    align-items: start;

    @media (--tablet) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (--desktop) {
      grid-template-columns: repeat(3, 1fr);
    }

    @media (--desktopXL) {
      grid-template-columns: repeat(4, 1fr);
    }

    & .tier {
      position: relative;
      width: 100%;

      & >>> button {
        width: 100%;
      }

      & .conflictmessage {
        display: block;
        opacity: 0;
        padding-top: 0.2rem;
        color: var(--colorErrorFlat);
        transition: opacity 0.1s ease;
        text-align: center;
        font-style: italic;
      }

      &.-conflict {
        & .conflictmessage {
          opacity: 1;
        }
      }
    }
  }
}

.customtierinputs {
  display: grid;
  justify-content: space-between;
  margin-top: var(--spacingMd);
  grid-template-columns: 1fr;
  grid-gap: 2rem;

  @media (--tablet) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (--desktop) {
    grid-gap: var(--spacingMd);
    grid-template-columns: repeat(3, 1fr);
  }
}

</style>
